import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Typography, Alert, Table, Space, Modal, Descriptions, Tag, message, Spin } from 'antd';
import { DatabaseOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import devToolsService, { DbBackupStatus } from '../services/devToolsService.ts';

const { Title, Text } = Typography;

const fmtSize = (bytes?: number | null) => {
  if (bytes == null) return '-';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`;
  return `${(bytes / 1024).toLocaleString('it-IT', { maximumFractionDigits: 0 })} KB`;
};
const fmtDate = (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm:ss') : '-');

/**
 * Strumenti → Backup Database (solo amministratore, v3.12.0).
 * Avvia un backup completo del database compresso in zip (<nome db>_<data_ora>.zip).
 * Il file resta SUL SERVER, in una cartella non pubblicata e raggiungibile solo via
 * ssh: da qui si vede solo l'esito (riuscito / fallito con il motivo).
 */
const BackupDatabase: React.FC = () => {
  const [status, setStatus] = useState<DbBackupStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | null>(null);
  const announced = useRef<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const data = await devToolsService.getBackupStatus();
      setStatus(data);
      // esito comunicato una volta, quando un backup avviato da questa pagina finisce
      const last = data.last;
      if (last && !data.running && announced.current === last.started_at) {
        announced.current = null;
        if (last.state === 'success') {
          Modal.success({ title: 'Backup completato', content: `File ${last.file_name} (${fmtSize(last.size_bytes)}) salvato sul server in ${last.directory}.` });
        } else if (last.state === 'failed') {
          Modal.error({ title: 'Backup fallito', content: last.error || 'Errore sconosciuto' });
        }
      }
    } catch (e: any) {
      if (!quiet) message.error(e?.response?.data?.detail || 'Errore nella lettura dello stato del backup');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // aggiornamento automatico mentre un backup è in corso
  useEffect(() => {
    if (status?.running) {
      timer.current = window.setTimeout(() => load(true), 3000);
    }
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [status, load]);

  const handleRun = () => {
    Modal.confirm({
      title: 'Eseguire il backup completo del database?',
      content: 'Il backup viene salvato sul server (accessibile solo via ssh). L\'applicazione resta utilizzabile durante l\'operazione.',
      okText: 'Esegui backup',
      cancelText: 'Annulla',
      onOk: async () => {
        setStarting(true);
        try {
          const data = await devToolsService.runBackup();
          announced.current = data.last?.started_at || null;
          setStatus(data);
          message.info('Backup avviato: l\'esito comparirà qui al termine.');
        } catch (e: any) {
          Modal.error({ title: 'Backup non avviato', content: e?.response?.data?.detail || 'Errore durante l\'avvio del backup' });
        } finally {
          setStarting(false);
        }
      },
    });
  };

  const last = status?.last;
  const stateTag = !last ? <Tag>nessun backup</Tag>
    : last.state === 'running' || status?.running ? <Tag icon={<LoadingOutlined />} color="processing">in corso</Tag>
    : last.state === 'success' ? <Tag icon={<CheckCircleOutlined />} color="success">riuscito</Tag>
    : <Tag icon={<CloseCircleOutlined />} color="error">fallito</Tag>;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}><DatabaseOutlined /> Backup Database</Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Backup completo del database (tabelle, dati, viste, procedure e trigger) compresso in un file zip con nome database + data e ora. Il file resta sul server in una cartella non raggiungibile dal web: si recupera solo via ssh."
      />
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<DatabaseOutlined />} loading={starting} disabled={!!status?.running} onClick={handleRun}>
            Esegui backup
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Aggiorna</Button>
        </Space>
        <Spin spinning={loading && !status}>
          <Descriptions bordered size="small" column={2} title="Ultimo backup">
            <Descriptions.Item label="Esito">{stateTag}</Descriptions.Item>
            <Descriptions.Item label="Avviato da">{last?.user || '-'}</Descriptions.Item>
            <Descriptions.Item label="Inizio">{fmtDate(last?.started_at)}</Descriptions.Item>
            <Descriptions.Item label="Fine">{fmtDate(last?.finished_at)}</Descriptions.Item>
            <Descriptions.Item label="File">{last?.file_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Dimensione">{fmtSize(last?.size_bytes)}{last?.sql_size_bytes ? <Text type="secondary"> (dump {fmtSize(last.sql_size_bytes)})</Text> : null}</Descriptions.Item>
            <Descriptions.Item label="Cartella sul server" span={2}><Text code>{status?.directory || '-'}</Text></Descriptions.Item>
            {last?.state === 'failed' && (
              <Descriptions.Item label="Motivo dell'errore" span={2}><Text type="danger">{last.error}</Text></Descriptions.Item>
            )}
          </Descriptions>
        </Spin>
      </Card>
      <Card title="Backup presenti sul server (ultimi 30)">
        <Table
          rowKey="file_name"
          size="small"
          dataSource={status?.backups || []}
          pagination={false}
          locale={{ emptyText: 'Nessun backup nella cartella' }}
          columns={[
            { title: 'File', dataIndex: 'file_name', key: 'file_name' },
            { title: 'Data', dataIndex: 'created_at', key: 'created_at', render: (v: string) => fmtDate(v) },
            { title: 'Dimensione', dataIndex: 'size_bytes', key: 'size_bytes', align: 'right' as const, render: (v: number) => fmtSize(v) },
          ]}
        />
      </Card>
    </div>
  );
};

export default BackupDatabase;
